import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { containers as api, orders as ordersApi, costItems as costItemsApi } from '../utils/api';
import { fmtBRL, fmtDate } from '../utils/helpers';
import { useToast, ToastContainer } from '../hooks/useToast';

const STATUS_META = {
  planning:    { label: 'Planejamento', bg: '#EEF2FF', color: '#3730A3' },
  booked:      { label: 'Reservado',    bg: '#E0F2FE', color: '#075985' },
  in_transit:  { label: 'Em trânsito',  bg: '#FEF9C3', color: '#854D0E' },
  cleared:     { label: 'Desembaraçado',bg: '#DCFCE7', color: '#166534' },
  delivered:   { label: 'Entregue',     bg: '#F0FDF4', color: '#15803D' },
  closed:      { label: 'Fechado',      bg: '#F3F4F6', color: '#374151' },
};
const STATUS_LIST = Object.keys(STATUS_META);

const CATEGORY_LABELS = {
  frete_internacional: 'Frete internacional',
  frete_nacional:      'Frete nacional',
  despachante:         'Despachante aduaneiro',
  armazenagem:         'Armazenagem',
  thc:                 'THC',
  siscomex:            'SISCOMEX',
  seguro:              'Seguro',
  impostos:            'Impostos',
  outros:              'Outros',
};
const BASIS_LABELS = { fob: 'Valor (FOB)', cbm: 'Volume (CBM)', weight: 'Peso', equal: 'Igual', manual: 'Manual' };

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.planning;
  return <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color }}>{m.label}</span>;
}

export default function Containers() {
  const { id } = useParams();
  if (id) return <ContainerDetail key={id} id={id} />;
  return <ContainerList />;
}

// ── Lista ───────────────────────────────────────────────────────────────────
function ContainerList() {
  const navigate = useNavigate();
  const { toasts, toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.list().then(rows => { setList(rows); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function handleCreate() {
    setCreating(true);
    try {
      const c = await api.create({ name: '' });
      navigate(`/containers/${c.id}`);
    } catch { toast('Erro ao criar container', 'error'); }
    finally { setCreating(false); }
  }

  if (loading) return <div className="page"><div className="text-muted">Carregando...</div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">🚢 Containers / Importações</div>
          <div className="page-subtitle">{list.length} importação(ões) consolidada(s)</div>
        </div>
        <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>+ Novo Container</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {list.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🚢</div>
            <div className="empty-title">Nenhum container</div>
            <div className="empty-sub">Crie uma importação e agrupe pedidos de fornecedores diferentes.</div>
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead>
                <tr>
                  <th>Código</th><th>Nome</th><th>Status</th><th>Pedidos</th>
                  <th>Total (BRL)</th><th>ETA</th><th>Container nº</th>
                </tr>
              </thead>
              <tbody>
                {list.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/containers/${c.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="mono">{c.code}</td>
                    <td className="bold">{c.name || '—'}</td>
                    <td><StatusPill status={c.status} /></td>
                    <td>{c.order_count}</td>
                    <td>{c.total_brl ? fmtBRL(c.total_brl) : '—'}</td>
                    <td className="text-muted">{c.eta ? fmtDate(c.eta) : '—'}</td>
                    <td className="mono">{c.container_number || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ToastContainer toasts={toasts} />
    </div>
  );
}

// ── Detalhe ───────────────────────────────────────────────────────────────────
function ContainerDetail({ id }) {
  const navigate = useNavigate();
  const { toasts, toast } = useToast();
  const [c, setC] = useState(null);
  const [form, setForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [availOrders, setAvailOrders] = useState([]);
  const [addOrderId, setAddOrderId] = useState('');
  const [newCost, setNewCost] = useState({ category: 'frete_internacional', description: '', amount: '', currency: 'BRL', exchange_rate: '', allocation_basis: 'cbm', paid_by: 'charth' });
  const [addingCost, setAddingCost] = useState(false);

  const load = useCallback(async () => {
    const detail = await api.get(id);
    setC(detail);
    setForm(detail);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    ordersApi.list({ limit: 9999 }).then(data => {
      const rows = data.rows || data;
      setAvailOrders(rows.filter(o => !o.container_id || o.container_id === id));
    }).catch(() => {});
  }, [id, c]);

  async function saveInfo() {
    setSavingInfo(true);
    try { await api.update(id, form); await load(); toast('Container salvo', 'success'); }
    catch { toast('Erro ao salvar', 'error'); }
    finally { setSavingInfo(false); }
  }

  async function addOrder() {
    if (!addOrderId) return;
    try { await api.addOrder(id, addOrderId); setAddOrderId(''); await load(); toast('Pedido adicionado', 'success'); }
    catch (e) { toast(e.response?.data?.error || 'Erro', 'error'); }
  }
  async function removeOrder(qid) {
    try { await api.removeOrder(id, qid); await load(); }
    catch { toast('Erro ao remover', 'error'); }
  }

  async function addCost() {
    if (!newCost.amount) return toast('Informe o valor', 'error');
    if (newCost.currency !== 'BRL' && !newCost.exchange_rate) return toast('Informe o câmbio para moeda estrangeira', 'error');
    setAddingCost(true);
    try {
      await api.addCost(id, {
        category: newCost.category, description: newCost.description,
        amount: parseFloat(newCost.amount), currency: newCost.currency,
        exchange_rate: newCost.exchange_rate ? parseFloat(newCost.exchange_rate) : undefined,
        allocation_basis: newCost.allocation_basis, paid_by: newCost.paid_by,
      });
      setNewCost({ ...newCost, description: '', amount: '', exchange_rate: '' });
      await load();
      toast('Custo adicionado e rateado', 'success');
    } catch (e) { toast(e.response?.data?.error || 'Erro', 'error'); }
    finally { setAddingCost(false); }
  }

  if (!c) return <div className="page"><div className="text-muted">Carregando...</div></div>;

  const inCandidates = availOrders.filter(o => !o.container_id);
  const field = (label, key, type = 'text') => (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <label style={{ fontSize: 11 }}>{label}</label>
      <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="gap-8">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/containers')}>← Containers</button>
          <div>
            <div className="page-title" style={{ fontFamily: 'var(--font-mono)', fontSize: 18 }}>{c.code}</div>
            <div className="page-subtitle">{c.members.length} pedido(s) · {c.totals.cbm.toFixed(2)} CBM · {c.totals.weight_kg.toFixed(0)} kg</div>
          </div>
        </div>
        <div className="action-group" style={{ alignItems: 'center', gap: 8 }}>
          <select value={form.status || 'planning'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--gray-200)' }}>
            {STATUS_LIST.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" onClick={saveInfo} disabled={savingInfo}>{savingInfo ? 'Salvando...' : '💾 Salvar'}</button>
        </div>
      </div>

      {/* Totais consolidados */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'CUSTO EFETIVO TOTAL', value: fmtBRL(c.totals.custo_efetivo), accent: true },
          { label: 'SALDO A PAGAR', value: fmtBRL(c.totals.balance) },
          { label: 'CUSTOS DO CONTAINER', value: fmtBRL(c.totals.cost_total_brl) },
          { label: 'CBM / PESO', value: `${c.totals.cbm.toFixed(2)} m³ · ${c.totals.weight_kg.toFixed(0)} kg` },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{k.label}</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: k.accent ? 'var(--pantone)' : 'inherit' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Logística */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 12 }}>LOGÍSTICA</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
          {field('Nome / descrição', 'name')}
          {field('Nº do container', 'container_number')}
          {field('Lacre', 'seal_number')}
          {field('Tipo', 'container_type')}
          {field('Armador / carrier', 'carrier')}
          {field('Navio', 'vessel')}
          {field('Booking', 'booking_ref')}
          {field('BL / AWB', 'bl_awb_number')}
          {field('Porto origem', 'port_origin')}
          {field('Porto destino', 'port_destination')}
          {field('ETD', 'etd', 'date')}
          {field('ETA', 'eta', 'date')}
          {field('Embarque real', 'actual_departure', 'date')}
          {field('Chegada real', 'actual_arrival', 'date')}
          {field('Nº da DI', 'di_number')}
        </div>
      </div>

      {/* Pedidos-membros */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 12 }}>PEDIDOS NESTE CONTAINER</div>
        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr><th>Pedido</th><th>Tipo</th><th>Fornecedor</th><th>CBM</th><th>Peso</th><th>Custo efetivo</th><th>Saldo</th><th></th></tr>
            </thead>
            <tbody>
              {c.members.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 20 }}>Nenhum pedido. Adicione abaixo.</td></tr>}
              {c.members.map(m => (
                <tr key={m.id}>
                  <td className="mono" style={{ cursor: 'pointer', color: 'var(--pantone)' }} onClick={() => navigate(`/orders/${m.id}`)}>{m.reference}</td>
                  <td>{m.order_type === 'sample' ? '🟠 Sample' : '🔵 Bulk'}</td>
                  <td>{m.supplier_name}</td>
                  <td>{(m.cbm || 0).toFixed(2)}</td>
                  <td>{(m.weight_kg || 0).toFixed(0)}</td>
                  <td>{m.custo_efetivo != null ? fmtBRL(m.custo_efetivo) : '—'}</td>
                  <td style={{ color: (m.total_balance || 0) > 0.01 ? '#C53030' : '#276749' }}>{m.total_balance != null ? fmtBRL(m.total_balance) : '—'}</td>
                  <td><button className="btn btn-ghost btn-sm btn-icon" onClick={() => removeOrder(m.id)} title="Remover do container">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={addOrderId} onChange={e => setAddOrderId(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--gray-200)', minWidth: 260 }}>
            <option value="">Selecione um pedido…</option>
            {inCandidates.map(o => <option key={o.id} value={o.id}>{o.reference} — {o.supplier_name} ({o.order_type})</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={addOrder} disabled={!addOrderId}>+ Adicionar pedido</button>
        </div>
      </div>

      {/* Custos do container + rateio */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 12 }}>CUSTOS DO CONTAINER (rateados entre os pedidos)</div>
        {c.cost_items.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
            <thead>
              <tr style={{ background: 'var(--gray-100)' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Categoria</th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Descrição</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Valor (BRL)</th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Rateio por</th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Paga</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {c.cost_items.map(ci => (
                <tr key={ci.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '6px 8px' }}>{CATEGORY_LABELS[ci.category] || ci.category}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--gray-500)' }}>{ci.description || '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtBRL(ci.amount_brl)}</td>
                  <td style={{ padding: '6px 8px' }}>{BASIS_LABELS[ci.allocation_basis] || ci.allocation_basis}</td>
                  <td style={{ padding: '6px 8px' }}>{ci.paid_by}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <button className="btn btn-ghost btn-sm btn-icon" title="Remover"
                      onClick={async () => { await costItemsApi.delete(ci.id); await load(); }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 8, alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Categoria</label>
            <select value={newCost.category} onChange={e => setNewCost(n => ({ ...n, category: e.target.value }))}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Descrição</label>
            <input value={newCost.description} onChange={e => setNewCost(n => ({ ...n, description: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Valor</label>
            <input type="number" step="0.01" value={newCost.amount} onChange={e => setNewCost(n => ({ ...n, amount: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Moeda</label>
            <select value={newCost.currency} onChange={e => setNewCost(n => ({ ...n, currency: e.target.value }))}>
              <option value="BRL">BRL</option><option value="USD">USD</option><option value="CNY">CNY</option>
            </select>
          </div>
          {newCost.currency !== 'BRL' && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Câmbio</label>
              <input type="number" step="0.0001" value={newCost.exchange_rate} onChange={e => setNewCost(n => ({ ...n, exchange_rate: e.target.value }))} />
            </div>
          )}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Rateio por</label>
            <select value={newCost.allocation_basis} onChange={e => setNewCost(n => ({ ...n, allocation_basis: e.target.value }))}>
              {Object.entries(BASIS_LABELS).filter(([k]) => k !== 'manual').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: 11 }}>Paga</label>
            <select value={newCost.paid_by} onChange={e => setNewCost(n => ({ ...n, paid_by: e.target.value }))}>
              <option value="charth">Charth</option><option value="fornecedor">Fornecedor</option>
              <option value="agente">Agente</option><option value="forwarder">Forwarder</option>
            </select>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={addCost} disabled={addingCost} style={{ marginBottom: 0 }}>+ Adicionar</button>
        </div>
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
